import {
  BreakpointsMap,
  Button as MauiButton,
  constants,
  Modal,
  Table as MauiTable,
  Text,
} from "@findemdev/maui";
import styled from "styled-components";
import type { StatusTone } from "./types";

const { colors, dimensions, typography } = constants;
const CONTROL_HEIGHT = dimensions.spacing["4"];
const CONTROL_GAP = dimensions["border-radius"].sm;
const CONTROL_HORIZONTAL_PADDING = dimensions.spacing["1"];
const CONTROL_VERTICAL_PADDING = dimensions["border-radius"].sm;
const CONTROL_ICON_SIZE = typography.header.defaultHeader["font-size"];
const CONTROL_PILL_RADIUS = dimensions.spacing["10"];
const TOOLBAR_CONTROL_WIDTH = `calc(${dimensions.spacing["10"]} + ${dimensions.spacing["5"]})`;
const CREATE_BUTTON_MIN_WIDTH = `calc(${dimensions.spacing["10"]} + ${dimensions.spacing["6"]})`;
const MODAL_HORIZONTAL_PADDING = dimensions.spacing["4"];
const MODAL_VERTICAL_PADDING = `calc(${dimensions.spacing["2"]} + ${dimensions["border-radius"].xs})`;
const MODAL_CLOSE_SIZE = dimensions.spacing["3"];
const PROJECT_FORM_BODY_TOP_PADDING = `calc(${dimensions.spacing["2"]} + ${dimensions["border-radius"].xs})`;
const PROJECT_FORM_CONTROL_HEIGHT = `calc(${dimensions.height.md} + ${dimensions["border-radius"].xs})`;
const PROJECT_FORM_CONTROL_HORIZONTAL_PADDING = `calc(${dimensions.spacing["1"]} + ${dimensions["border-radius"].xs})`;
const PROJECT_FORM_CONTENT_GAP = `calc(${dimensions.spacing["1"]} + ${dimensions["border-radius"].xs})`;
const PROJECT_FORM_FOCUS_RING_WIDTH = `calc(${dimensions["border-radius"].xs} / 2)`;
const PROJECT_FORM_TEXT_GAP = `calc(${dimensions["border-radius"].xs} / 2)`;
const PROJECT_FORM_CLIENT_MODE_WIDTH = `calc(${dimensions.spacing["10"]} + ${dimensions.spacing["10"]} + ${dimensions.spacing["10"]} + ${dimensions.spacing["2"]} + ${dimensions["border-radius"].xs})`;
const PROJECT_FORM_INTERNAL_MODAL_MIN_HEIGHT = `calc(${dimensions.spacing["10"]} * 8)`;
const COMPANY_LOGO_SIZE = dimensions.spacing["3"];
const HEADLINE_FONT_SIZE = `calc(${typography.header.defaultHeader["font-size"]} + ${dimensions["border-radius"].sm})`;
const ICON_BUTTON_SIZE = dimensions.spacing["4"];
const PAGE_HEADER_GAP = PROJECT_FORM_TEXT_GAP;
const SEARCH_FIELD_WIDTH = `calc(${dimensions.spacing["10"]} + ${dimensions.spacing["10"]} + ${dimensions.spacing["5"]} + ${dimensions.spacing["2"]} + ${dimensions["border-radius"].xs})`;
const SEARCH_INPUT_HORIZONTAL_PADDING = `calc(${dimensions.spacing["1"]} + ${PROJECT_FORM_FOCUS_RING_WIDTH})`;
const STATUS_CHIP_VERTICAL_PADDING = `calc(${dimensions["border-radius"].sm} / 2)`;
const TABLE_HEADER_HEIGHT = dimensions.spacing["5"];
const PIPELINE_SKELETON_WIDTH = dimensions.spacing["4"];
const IMPERSONATION_FIELD_WIDTH = `calc(${dimensions.spacing["10"]} + ${dimensions.spacing["10"]} + ${dimensions.spacing["10"]})`;

export const Page = styled.div`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  padding: ${dimensions.spacing["3"]} ${dimensions.spacing["8"]};
  width: 100%;

  @media (max-width: ${BreakpointsMap.md}px) {
    padding: ${dimensions.spacing["3"]} ${dimensions.spacing["2"]};
  }
`;

export const PageInner = styled.main`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
`;

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${PAGE_HEADER_GAP};
  margin-bottom: ${dimensions.spacing["3"]};
`;

export const Eyebrow = styled(Text).attrs({ variant: "lead" })`
  color: ${colors["grey-500"]};
`;

export const Headline = styled(Text).attrs({ as: "h1", variant: "default" })`
  color: ${colors["grey-700"]};
  font-size: ${HEADLINE_FONT_SIZE};
  line-height: ${dimensions.spacing["4"]};
  margin: 0;

  strong {
    font-weight: 600;
  }
`;

export const Toolbar = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions["border-radius"].xs};
  justify-content: space-between;
  margin-bottom: ${dimensions.spacing["1"]};

  @media (max-width: ${BreakpointsMap.md}px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const ToolbarLeft = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: ${dimensions.spacing["1"]};
  min-width: 0;

  @media (max-width: ${BreakpointsMap.md}px) {
    flex-wrap: wrap;
  }
`;

export const ToolbarRight = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: ${dimensions.spacing["1"]};
  justify-content: flex-end;
  min-width: 0;

  @media (max-width: ${BreakpointsMap.md}px) {
    justify-content: flex-start;
  }
`;

export const SearchField = styled.div`
  flex: 0 0 ${SEARCH_FIELD_WIDTH};
  width: ${SEARCH_FIELD_WIDTH};

  .ant-input-affix-wrapper {
    border-color: ${colors["grey-300"]};
    border-radius: ${dimensions["border-radius"].md};
    box-shadow: none;
    height: ${CONTROL_HEIGHT};
    padding: ${dimensions["border-radius"].sm} ${SEARCH_INPUT_HORIZONTAL_PADDING};
  }

  .ant-input {
    color: ${colors["grey-650"]};
  }

  @media (max-width: ${BreakpointsMap.md}px) {
    flex: 1 1 100%;
    max-width: none;
    width: 100%;
  }
`;

export const FilterField = styled.div<{ $pill?: boolean }>`
  min-width: ${TOOLBAR_CONTROL_WIDTH};
  width: ${TOOLBAR_CONTROL_WIDTH};

  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    border-color: ${({ $pill }) =>
      $pill ? colors["grey-300"] : colors["grey-400"]} !important;
    border-radius: ${({ $pill }) =>
      $pill ? CONTROL_PILL_RADIUS : dimensions["border-radius"].md} !important;
    height: ${CONTROL_HEIGHT} !important;
    padding: 0 ${CONTROL_HORIZONTAL_PADDING} !important;
  }

  .ant-select-selection-item {
    color: ${colors["grey-650"]};
    line-height: ${dimensions.height.sm} !important;
  }

  .ant-select-arrow {
    color: ${colors["grey-450"]};
  }
`;

export const FilterFieldLabel = styled(Text).attrs({
  as: "label",
  options: ["muted"],
  variant: "detail",
})`
  display: block;
  margin-bottom: ${dimensions["border-radius"].xs};
`;

export const ImpersonationField = styled(FilterField)`
  min-width: ${IMPERSONATION_FIELD_WIDTH};
  width: ${IMPERSONATION_FIELD_WIDTH};
`;

export const OrgField = styled(ImpersonationField)``;

export const CreateButton = styled(MauiButton)`
  && {
    align-items: center;
    background-color: ${colors["blue-300"]} !important;
    border-color: ${colors["blue-300"]} !important;
    border-radius: ${dimensions["border-radius"].md};
    box-shadow: none;
    color: ${colors.white} !important;
    display: inline-flex;
    gap: ${CONTROL_GAP};
    height: ${CONTROL_HEIGHT};
    justify-content: center;
    min-width: ${CREATE_BUTTON_MIN_WIDTH};
    padding: ${CONTROL_VERTICAL_PADDING} ${CONTROL_HORIZONTAL_PADDING};
    text-shadow: none;
    white-space: nowrap;
  }

  &&:hover,
  &&:focus {
    background-color: ${colors["blue-300"]} !important;
    border-color: ${colors["blue-300"]} !important;
    color: ${colors.white} !important;
  }

  && .ant-btn-icon,
  && .material-icons,
  && .material-icons-round {
    align-items: center;
    display: inline-flex;
    font-size: ${CONTROL_ICON_SIZE};
    height: ${CONTROL_ICON_SIZE};
    line-height: ${CONTROL_ICON_SIZE};
    margin: 0 !important;
    width: ${CONTROL_ICON_SIZE};
  }

  && .ant-btn-icon + span,
  && > span + span {
    margin-left: 0 !important;
  }
`;

export const TableSection = styled.section`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const TableViewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  width: 100%;
`;

export const StyledTable = styled(MauiTable)<{ $fillRows?: boolean }>`
  width: 100%;

  .ant-table {
    background: ${colors.white};
    color: ${colors["grey-700"]};
  }

  .ant-table-container table > thead > tr:first-child th:first-child {
    border-start-start-radius: 0;
  }

  .ant-table-container table > thead > tr:first-child th:last-child {
    border-start-end-radius: 0;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-thead > tr > th {
    background: ${colors.white};
    border-bottom: 1px solid ${colors["grey-400"]};
    color: ${colors["grey-500"]};
    height: ${TABLE_HEADER_HEIGHT};
    padding: 0 ${dimensions.spacing["1"]};
  }

  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    padding-left: ${PROJECT_FORM_CONTROL_HORIZONTAL_PADDING};
  }

  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr > td:last-child {
    padding-left: 0;
    padding-right: 0;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${colors["grey-300"]};
    color: ${colors["grey-700"]};
    height: ${({ $fillRows }) =>
      $fillRows
        ? `clamp(${dimensions.spacing["7"]}, 6dvh, ${dimensions.spacing["9"]})`
        : dimensions.height.lg};
    padding: 0 ${dimensions.spacing["1"]};
    transition: none;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${colors["grey-125"]};
  }

  .ant-table-tbody > tr {
    cursor: pointer;
  }
`;

export const ColumnHeader = styled(Text).attrs({ as: "span" })<{
  $emphasis?: boolean;
}>`
  color: ${({ $emphasis }) =>
    $emphasis ? colors["grey-700"] : colors["grey-500"]};
  display: inline-flex;
  max-width: 100%;

  ${({ $emphasis }) => ($emphasis ? "font-weight: 500;" : "")}
`;

export const SortableColumnHeader = styled.button<{
  $active?: boolean;
}>`
  align-items: center;
  background: none;
  border: none;
  color: ${({ $active }) => ($active ? colors["grey-700"] : colors["grey-500"])};
  cursor: pointer;
  display: inline-flex;
  font-weight: ${({ $active }) => ($active ? 600 : "inherit")};
  gap: 4px;
  padding: 0;

  &:hover {
    color: ${colors["grey-700"]};
  }

  &:disabled {
    color: ${colors["grey-500"]};
    cursor: default;
  }
`;

export const CellText = styled(Text).attrs({ as: "span" })<{
  $emphasis?: boolean;
  $link?: boolean;
  $muted?: boolean;
}>`
  color: ${({ $muted }) =>
    $muted ? colors["grey-500"] : "inherit"};
  display: block;
  font-weight: ${({ $emphasis }) => ($emphasis ? 500 : "inherit")};
  overflow: hidden;
  text-decoration: ${({ $link }) => ($link ? "underline" : "none")};
  text-decoration-skip-ink: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PipelineSkeletonCell = styled.span`
  align-items: center;
  display: inline-flex;
  height: ${dimensions.spacing["3"]};
  justify-content: flex-start;
  line-height: 0;
  width: ${PIPELINE_SKELETON_WIDTH};

  && .ant-skeleton,
  && .ant-skeleton-input {
    display: block;
    line-height: 1;
    min-width: 0 !important;
    vertical-align: top;
    width: 100% !important;
  }

  && .ant-skeleton-input {
    height: ${dimensions.spacing["2"]} !important;
  }
`;

export const ProjectNameButton = styled(MauiButton)`
  && {
    background: none !important;
    border: 0 !important;
    box-shadow: none;
    color: ${colors["grey-700"]};
    display: block;
    font: inherit;
    font-weight: 500;
    height: auto;
    max-width: 100%;
    overflow: hidden;
    padding: 0;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  cursor: pointer;

  &:hover {
    color: ${colors["grey-700"]} !important;
  }
`;

export const CompanyCell = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions.spacing["1"]};
  min-width: 0;
`;

export const CompanyLogoImage = styled.img`
  background-color: ${colors.white};
  border: 1px solid ${colors["grey-300"]};
  border-radius: 50%;
  flex: 0 0 ${COMPANY_LOGO_SIZE};
  height: ${COMPANY_LOGO_SIZE};
  object-fit: contain;
  overflow: hidden;
  width: ${COMPANY_LOGO_SIZE};
`;

export const ProjectCompanyOption = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions.spacing["1"]};
  min-width: 0;
`;

export const ProjectCompanyOptionLabel = styled(Text).attrs({ as: "span" })`
  color: ${colors["grey-700"]};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StatusChip = styled(Text).attrs({ as: "span" })<{
  $tone: StatusTone;
  $muted?: boolean;
}>`
  background-color: ${({ $tone }) => {
    if ($tone === "green") return colors["green-100"];
    if ($tone === "blue") return colors["blue-100"];
    if ($tone === "yellow") return colors["orange-200"];
    return colors["grey-200"];
  }};
  border-radius: ${CONTROL_PILL_RADIUS};
  color: ${({ $tone, $muted }) => {
    if ($muted) return colors["grey-500"];
    if ($tone === "green") return colors["green-400"];
    if ($tone === "blue") return colors["blue-400"];
    if ($tone === "yellow") return colors["orange-400"];
    return colors["grey-400"];
  }};
  display: inline-flex;
  gap: 5px;
  max-width: 100%;
  align-items: center;
  overflow: hidden;
  padding: ${STATUS_CHIP_VERTICAL_PADDING} ${dimensions.spacing["1"]};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ActionCell = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

export const IconButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: ${dimensions["border-radius"].xs};
  color: ${colors["grey-500"]};
  cursor: pointer;
  display: inline-flex;
  height: ${ICON_BUTTON_SIZE};
  justify-content: center;
  padding: 0;
  width: ${ICON_BUTTON_SIZE};

  &:hover:not(:disabled) {
    background-color: ${colors["grey-150"]};
    color: ${colors["grey-700"]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const MenuItemContent = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions.spacing["1"]};
`;

export const LoadingState = styled.div`
  align-items: center;
  border-top: 1px solid ${colors["grey-200"]};
  color: ${colors["grey-500"]};
  display: flex;
  flex: 1;
  gap: ${dimensions.spacing["1"]};
  justify-content: center;
  min-height: 0;
`;

export const EmptyState = styled.div`
  align-items: center;
  border-top: 1px solid ${colors["grey-200"]};
  color: ${colors["grey-500"]};
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${dimensions.spacing["1"]};
  justify-content: center;
  min-height: 0;
  text-align: center;
`;

export const EmptyStateTitle = styled(Text).attrs({ variant: "lead" })`
  color: ${colors["grey-700"]};
  font-weight: 600;
`;

export const EmptyStateDescription = styled(Text)`
  color: ${colors["grey-500"]};
`;

export const Pagination = styled.nav`
  align-items: center;
  display: flex;
  gap: ${dimensions.spacing["1"]};
  justify-content: flex-end;
  padding-top: ${dimensions.spacing["1"]};
`;

export const PaginationEllipsis = styled(Text).attrs({ as: "span" })`
  color: ${colors["grey-500"]};
  padding: 0 ${dimensions.spacing["1"]};
`;

export const PageButton = styled(MauiButton)<{ $active?: boolean }>`
  align-items: center !important;
  background-color: ${({ $active }) =>
    $active ? colors["blue-300"] : colors.white} !important;
  border: 1px solid ${({ $active }) =>
    $active ? colors["blue-300"] : colors["grey-300"]} !important;
  border-radius: ${dimensions["border-radius"].sm};
  box-shadow: none;
  color: ${({ $active }) =>
    $active ? colors.white : colors["grey-700"]} !important;
  cursor: pointer;
  display: inline-flex !important;
  height: ${dimensions.height.sm} !important;
  justify-content: center !important;
  min-width: ${dimensions.height.sm};
  padding: ${dimensions["border-radius"].xs} !important;

  ${({ $active }) => ($active ? "font-weight: 600;" : "")}

  &:hover:not(:disabled) {
    background-color: ${({ $active }) =>
      $active ? colors["blue-350"] : colors["grey-100"]} !important;
    border-color: ${({ $active }) =>
      $active ? colors["blue-350"] : colors["grey-400"]} !important;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: ${dimensions["border-radius"].lg};
  }

  .ant-modal-footer {
    border-top: 1px solid ${colors["grey-200"]};
  }
`;

export const FormGrid = styled.div`
  display: grid;
  gap: ${dimensions.spacing["2"]};
`;

export const FormField = styled(Text).attrs({
  as: "label",
  variant: "boldData",
})`
  color: ${colors["grey-700"]};
  display: grid;
  gap: ${dimensions["border-radius"].sm};
`;

export const ProjectFormModalFrame = styled(Modal)<{
  $isInternalCreate?: boolean;
}>`
  .ant-modal-content {
    background-color: ${colors.white};
    border-radius: ${dimensions["border-radius"].lg};
    display: flex;
    flex-direction: column;
    min-height: ${({ $isInternalCreate }) =>
      $isInternalCreate ? PROJECT_FORM_INTERNAL_MODAL_MIN_HEIGHT : "auto"};
    overflow: hidden;
    padding: 0;
  }

  .ant-modal-header {
    background-color: ${colors.white};
    margin: 0;
    padding: ${dimensions.spacing["3"]} ${MODAL_HORIZONTAL_PADDING} 0;
  }

  .ant-modal-title {
    color: ${colors.black};
    padding-bottom: ${dimensions.spacing["2"]};
  }

  .ant-modal-close {
    color: ${colors["grey-500"]};
    height: ${MODAL_CLOSE_SIZE};
    right: ${MODAL_HORIZONTAL_PADDING};
    top: ${dimensions.spacing["3"]};
    width: ${MODAL_CLOSE_SIZE};
  }

  .ant-modal-close-x {
    align-items: center;
    display: flex;
    height: ${MODAL_CLOSE_SIZE};
    justify-content: center;
    line-height: ${MODAL_CLOSE_SIZE};
    width: ${MODAL_CLOSE_SIZE};
  }

  .ant-modal-body {
    flex: 1;
    padding: ${PROJECT_FORM_BODY_TOP_PADDING} ${MODAL_HORIZONTAL_PADDING} 0;
  }

  .ant-modal-footer {
    border-top: 0;
    margin: 0;
    padding: ${dimensions.spacing["1"]} ${MODAL_HORIZONTAL_PADDING}
      ${MODAL_VERTICAL_PADDING};
    text-align: right;
  }
`;

export const ProjectFormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${PROJECT_FORM_CONTENT_GAP};
`;

export const ProjectFormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${dimensions["border-radius"].xs};
  width: 100%;
`;

export const ProjectFormText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${PROJECT_FORM_TEXT_GAP};
`;

export const ProjectFormLabelRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions["border-radius"].xs};
`;

export const ProjectFormLabel = styled(Text).attrs({ as: "label" })`
  color: ${colors["grey-700"]};
  font-weight: ${typography.header.defaultHeader["font-weight"]};
`;

export const ProjectFormGroupLabel = styled(Text).attrs({ as: "span" })`
  color: ${colors["grey-700"]};
  font-weight: ${typography.header.defaultHeader["font-weight"]};
`;

export const ProjectFormHelpText = styled(Text).attrs({
  as: "span",
  variant: "detail",
})`
  color: ${colors["grey-500"]};
`;

export const ProjectFormAgentContextBadge = styled(Text).attrs({
  as: "span",
  variant: "detail",
})`
  background-color: ${colors["blue-100"]};
  border-radius: ${dimensions["border-radius"].sm};
  color: ${colors["blue-400"]};
  padding: 0 ${dimensions["border-radius"].sm};
`;

export const ProjectFormClientModeControl = styled.div`
  max-width: 100%;
  width: ${PROJECT_FORM_CLIENT_MODE_WIDTH};

  && .ant-radio-group {
    background-color: ${colors["grey-150"]};
    border: 1px solid ${colors["grey-400"]};
    border-radius: ${dimensions["border-radius"].md};
    display: grid;
    gap: ${dimensions["border-radius"].xs};
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: ${PROJECT_FORM_FOCUS_RING_WIDTH};
    width: 100%;
  }

  && .ant-radio-button-wrapper {
    align-items: center;
    background-color: transparent !important;
    border: 1px solid transparent !important;
    border-radius: ${dimensions["border-radius"].sm} !important;
    box-shadow: none !important;
    color: ${colors["grey-500"]} !important;
    display: inline-flex;
    height: ${dimensions.height.sm};
    justify-content: center;
    margin: 0;
    padding: 0 ${dimensions.spacing["1"]};
  }

  && .ant-radio-button-wrapper:not(:first-child)::before {
    display: none;
  }

  && .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background-color: ${colors.white} !important;
    border-color: ${colors["grey-300"]} !important;
    color: ${colors["grey-700"]} !important;
  }

  &&
    .ant-radio-button-wrapper:not(
      .ant-radio-button-wrapper-checked,
      .ant-radio-button-wrapper-disabled
    ):hover {
    background-color: ${colors["grey-200"]} !important;
    color: ${colors["grey-700"]} !important;
  }

  && .ant-radio-button-wrapper:focus-within {
    outline: ${PROJECT_FORM_FOCUS_RING_WIDTH} solid ${colors["blue-200"]};
    outline-offset: 0;
  }

  && .ant-radio-button-wrapper-disabled {
    color: ${colors["grey-450"]} !important;
    cursor: not-allowed;
  }
`;

export const ProjectFormControl = styled.div`
  width: 100%;

  > .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector {
    align-items: center;
    background-color: ${colors.white} !important;
    border-color: ${colors["grey-400"]} !important;
    border-radius: ${dimensions["border-radius"].md} !important;
    box-shadow: none !important;
    display: flex;
    height: ${PROJECT_FORM_CONTROL_HEIGHT} !important;
    padding: 0 ${PROJECT_FORM_CONTROL_HORIZONTAL_PADDING} !important;
  }

  > .ant-input:focus,
  > .ant-input:hover,
  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper:hover,
  .ant-select-focused .ant-select-selector,
  .ant-select-selector:focus,
  .ant-select-selector:hover {
    border-color: ${colors["blue-300"]} !important;
    box-shadow: 0 0 0 ${PROJECT_FORM_FOCUS_RING_WIDTH}
      ${colors["blue-100"]} !important;
  }

  .ant-input,
  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-select-selection-search-input {
    color: ${colors["grey-700"]};
  }

  .ant-input::placeholder,
  .ant-select-selection-placeholder {
    color: ${colors["grey-500"]};
    opacity: 1;
  }

  .ant-select {
    width: 100%;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    align-items: center;
    display: flex;
  }

  .ant-select-selection-search-input {
    height: ${PROJECT_FORM_CONTROL_HEIGHT} !important;
  }

  .ant-select-arrow {
    color: ${colors["grey-450"]};
    right: ${PROJECT_FORM_CONTROL_HORIZONTAL_PADDING};
  }
`;

export const ProjectFormDropdownLoader = styled.div`
  align-items: center;
  border-top: 1px solid ${colors["grey-200"]};
  color: ${colors["grey-500"]};
  display: flex;
  gap: ${dimensions.spacing["1"]};
  justify-content: center;
  padding: ${dimensions.spacing["1"]};
`;

export const ProjectFormCancelButton = styled(MauiButton)`
  && {
    align-items: center;
    background-color: ${colors.white} !important;
    border: 1px solid ${colors["grey-400"]} !important;
    border-radius: ${dimensions["border-radius"].md};
    box-shadow: none;
    color: ${colors["grey-700"]} !important;
    display: inline-flex;
    height: ${PROJECT_FORM_CONTROL_HEIGHT};
    justify-content: center;
    padding: 0 ${PROJECT_FORM_CONTROL_HORIZONTAL_PADDING};
    text-shadow: none;
  }

  &&:hover,
  &&:focus {
    background-color: ${colors.white} !important;
    border-color: ${colors["grey-400"]} !important;
    color: ${colors["grey-700"]} !important;
  }
`;

export const ProjectFormSubmitButton = styled(MauiButton)`
  && {
    align-items: center;
    background-color: ${colors["blue-300"]} !important;
    border: 1px solid ${colors["blue-300"]} !important;
    border-radius: ${dimensions["border-radius"].md};
    box-shadow: none;
    color: ${colors.white} !important;
    display: inline-flex;
    height: ${PROJECT_FORM_CONTROL_HEIGHT};
    justify-content: center;
    padding: 0 ${PROJECT_FORM_CONTROL_HORIZONTAL_PADDING};
    text-shadow: none;
  }

  &&:hover,
  &&:focus {
    background-color: ${colors["blue-300"]} !important;
    border-color: ${colors["blue-300"]} !important;
    color: ${colors.white} !important;
  }

  &&:disabled {
    opacity: 0.45;
  }
`;

export const DeleteCopy = styled(Text).attrs({ as: "p" })`
  color: ${colors["grey-600"]};
  margin: 0;

  strong {
    color: ${colors["grey-700"]};
  }
`;
